import { useContext } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { AmplitudeContext, ThemeContext } from '@/src/contexts'

import { Avatar, Popover, Typography } from 'antd'
import { PhysicianAddress } from '@/src/components'

import { trackPhysicianEvents } from '@/src/lib'

import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
config.autoAddCss = false
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '@/src/lib'

import { GeoLocation, Physician } from '@/src/interfaces'

const { Link, Text, Title } = Typography

type PhysicianBioProps = {
  physician: Physician
  selectedPhysician?: Physician | null
  position?: number
  geoLocation?: GeoLocation | null
}

const PhysicianName = ({
  physician,
  selectedPhysician,
  isDarkMode,
  onClick,
}: {
  physician: Physician
  selectedPhysician?: Physician | null
  isDarkMode: boolean
  onClick?: () => void
}) => {
  if (selectedPhysician) {
    return (
      <Title
        level={3}
        className='custom-physician-bio-name-title ant-typography'
        style={{
          color: isDarkMode ? '#38A7C8' : '#274765',
        }}
      >
        Dr. {physician.full_name}
        {physician.degree ? `, ${physician.degree}` : ''}
      </Title>
    )
  }

  return (
    <Title
      onClick={onClick}
      level={3}
      className='custom-physician-bio-name-title ant-typography cursor-pointer hover:underline'
      style={{
        color: isDarkMode ? '#38A7C8' : '#274765',
      }}
    >
      {physician.full_name}
    </Title>
  )
}

const PhysicianSpecialty = ({ specialty }: { specialty: string }) => {
  return (
    <div className='flex flex-col lg:flex-row justify-start items-start lg:items-center gap-0 lg:gap-4'>
      <Title
        type='secondary'
        className='custom-physician-bio-specialty-title ant-typography'
      >
        {specialty
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')}
      </Title>
    </div>
  )
}

export const PhysicianBio = ({
  physician,
  selectedPhysician,
  position,
  geoLocation,
}: PhysicianBioProps) => {
  const { isDarkMode } = useContext(ThemeContext)
  const router = useRouter()
  const pathname = usePathname()
  const { trackAmplitudeEvent } = useContext(AmplitudeContext)

  const handlePhysicianClick = () => {
    sessionStorage.setItem(
      `physician_${physician.npi}`,
      JSON.stringify(physician)
    )
    // Amplitude - Track the profile link clicked event
    trackPhysicianEvents.profileLinkClicked(trackAmplitudeEvent)({
      ...physician,
      position: position ?? 'Position Not Available',
    })
    router.push(`search/physician/${physician.npi}`)
  }

  const content = (
    <div className='max-w-sm'>
      <p>
        <span
          style={{ fontWeight: '700', fontStyle: 'italic', color: '#06686b' }}
        >
          PhysicianGPS
        </span>{' '}
        uses advanced AI to analyze billions of data points and find the top 25%
        of doctors in the U.S. This means you get access to the best physicians,
        ensuring high-quality care for you and your family.
      </p>
    </div>
  )

  const PhysicianNameAndSpecialty = () => {
    return (
      <div>
        <PhysicianName
          data-testid='physician-profile-link'
          physician={physician}
          selectedPhysician={selectedPhysician}
          isDarkMode={isDarkMode}
          onClick={handlePhysicianClick}
        />
        <PhysicianSpecialty specialty={physician.specialty} />
        <Popover content={content} trigger='click' color='#f2eee4'>
          <Link className='custom-top-tier-link'>
            <FontAwesomeIcon icon='award' className='mr-1 text-primary' />
            <span className='custom-top-tier-link'>Top Tier</span>
          </Link>
        </Popover>
      </div>
    )
  }

  const PhysicianAvatar = () => {
    return (
      <>
        {selectedPhysician ? (
          <Avatar
            size={{ xs: 80, sm: 80, md: 120, lg: 120, xl: 120, xxl: 120 }}
            src={
              physician.image_url
                ? physician.image_url
                : '/assets/avatars/physician-avatar.png'
            }
          />
        ) : (
          <Avatar
            data-testid='physician-avatar'
            size={{ xs: 60, sm: 60, md: 100, lg: 100, xl: 120, xxl: 120 }}
            style={{ cursor: 'pointer' }}
            src={
              physician.image_url
                ? physician.image_url
                : '/assets/avatars/physician-avatar.png'
            }
            className='transition delay-100 hover:brightness-50'
            onClick={handlePhysicianClick}
            alt={physician.full_name ? physician.full_name : 'Physician Avatar'}
          />
        )}
      </>
    )
  }

  return (
    <div className='flex flex-col gap-2 md:flex-row md:gap-6 print:!block print:!w-full'>
      <div className='flex flex-row gap-4 md:block min-w-fit'>
        <div className='mb-0 md:mb-8'>
          <PhysicianAvatar />
        </div>
        <div className='flex flex-col md:hidden' data-testid='mobile-section'>
          <PhysicianNameAndSpecialty />
        </div>
      </div>
      <div className='flex flex-col justify-between'>
        <div className='hidden md:flex flex-col' data-testid='desktop-section'>
          <PhysicianNameAndSpecialty />
          {selectedPhysician && pathname !== `/physician/${physician.npi}` && (
            <Text
              className='custom-physician-bio-address-text ant-typography'
              type='secondary'
            >
              <FontAwesomeIcon icon='location-dot' className='mr-2' />
              {physician.address}
            </Text>
          )}
        </div>
        {!selectedPhysician && (
          <div className='flex flex-col gap-1 overflow-hidden'>
            <PhysicianAddress
              physician={physician}
              geoLocation={geoLocation}
              isProfileCard={false}
            />
          </div>
        )}
      </div>
    </div>
  )
}
